import React, {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {connect, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  Animated,
  BackHandler,
  Dimensions,
  Easing,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {tr} from '../../translations';
import Config from 'react-native-config';
import {endSession} from '../../screens/auth/login/actions';
import {closeSettingsModal} from './actions';
import {SettingsModalProps} from '../../interfaces/connected-components/settingsModal';
import ChangePassword from '../../screens/content/changePassword';
import FontChanger from '../../screens/content/fontChanger';
import SideMenuListItem from '../../components/sideMenuListItem';
import Profile from '../../screens/content/profile';
import Icon from '../../components/icon';
import {getFontFamily} from '../../utils/helpers';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {openQuestionModal} from '../questionModal/actions';
import {modalTypes} from '../../utils/enums';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetFlatList,
} from '@gorhom/bottom-sheet';
import {isWeb} from '../../utils/responsive';
import BottomSheetHandle from '../../components/bottomSheetHandle';

const Separator = styled.View`
  width: 100%;
  align-self: center;
  height: 2px;
  background-color: ${({theme}) => theme.border + '33'};
`;
const Space = styled.View<{height: number}>`
  align-self: center;
  height: ${({height}) => height}px;
`;

// Web-specific header row
const WebHeaderRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom-width: 2px;
  border-bottom-color: ${({theme}) => theme.border + '33'};
`;

// Web-specific close button
const WebCloseButton = styled.TouchableOpacity`
  padding: 8px;
`;

// Web-specific scrollable area
const WebScrollView = styled.ScrollView`
  width: 100%;
`;

const AnimatedTitleItem = Animated.createAnimatedComponent(Text);
const AnimatedTitle = styled(AnimatedTitleItem)`
  font-size: ${({theme}) => theme.text.s6}px;
  font-family: ${getFontFamily().bold};
  ${Platform.OS === 'web' && `font-weight:700;`}
  color: ${({theme}) => theme.text.dark};
  letter-spacing: 1px;
  text-align: center;
  width: 100%;
  padding-top: 1%;
  padding-bottom: 3%;
  padding-horizontal: 5%;
  flex: 1;
`;
const LeftScreen = styled(Animated.View)<{width: number}>`
  width: ${({width}) => width}px;
  height: 100%;
  align-self: center;
  flex: 1;
  z-index: 1;
`;
const RightScreen = styled(Animated.View)<{isVisible?: boolean; width: number}>`
  width: ${({width}) => width}px;
  height: 100%;
  align-self: center;
  flex: 1;
  z-index: 2;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${({theme, isVisible}) =>
    isVisible ? theme.screenBackground : 'transparent'};
`;
const Row = styled.View`
  width: 94%;
  align-self: center;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  padding-bottom: 2%;
`;
const CloseIconWrapper = styled.View`
  position: relative;
  z-index: 99;
`;
const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity);
const CloseIconContainer = styled(AnimatedTouchable)`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  padding: ${({theme}) => theme.text.s5 * 0.7}px;
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
`;

const ProfileScreen = () => <Profile />;
const ChangePasswordScreen = () => <ChangePassword fromDrawer />;
const FontChangerScreen = () => <FontChanger />;

// Define the type for a settings item
interface SettingsItem {
  iconName: string;
  label: string;
  component: () => JSX.Element | null;
}

const settingsList = (email: string): (SettingsItem | false)[] => [
  {
    iconName: 'accountMenu',
    label: 'profilePage',
    component: ProfileScreen,
  },
  Config.DEMO_EMAIL !== email && {
    iconName: 'changePass',
    label: 'changePasswordPage',
    component: ChangePasswordScreen,
  },
  {
    iconName: 'menuTextSize',
    label: 'fontSizePage',
    component: FontChangerScreen,
  },
  // {
  //   iconName: 'logout',
  //   label: 'logout',
  //   component: () => null,
  // },
];

const SettingsModal = ({settingsModal}: SettingsModalProps) => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  const {wdp, hdp, getByScreenSize, isMobile} = useResponsiveDimensions();
  const {isVisible, title, component} = settingsModal || {};
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const sheetRef = useRef<BottomSheet>(null);
  const snapPoints = useMemo(
    () => [Dimensions.get('screen').height - Math.max(topInset || 0, hdp(6))],
    [],
  );
  const {email} =
    useSelector((state: RootState) => state.auth)?.currentUser || {};
  const [activeComponent, setActiveComponent] = useState<SettingsItem | null>(
    null,
  );

  // Filter settings list to remove false items and cast to the correct type
  const filteredSettingsList = useMemo(() => {
    return settingsList(email || '').filter(Boolean) as SettingsItem[];
  }, [email]);

  // Animation values
  const slideAnim = useRef(
    new Animated.Value(wdp(getByScreenSize(99, 40))),
  ).current;
  const leftScreenOpacity = useRef(new Animated.Value(1)).current;
  const backButtonScale = useRef(new Animated.Value(0)).current;
  const backButtonRotate = useRef(new Animated.Value(0)).current;
  const titleSlideX = useRef(new Animated.Value(0)).current;
  const titleOpacity = useRef(new Animated.Value(1)).current;

  // Calculate web modal width based on screen size
  const webModalWidth = useMemo(() => {
    if (isWeb && !isMobile && typeof window !== 'undefined') {
      const screenWidth = window.innerWidth;
      // Use a responsive width that's appropriate for the device
      return Math.min(screenWidth * 0.9, 900); // Max 900px, or 90% of screen width
    }
    return 500;
  }, [isMobile]);

  useEffect(() => {
    if (isVisible) {
      if (!(isWeb && !isMobile) && sheetRef.current) {
        sheetRef.current?.snapToIndex(0);
      }
    } else {
      if (!(isWeb && !isMobile) && sheetRef.current) {
        sheetRef.current?.close();
      }
      // Reset animation states when modal is closed
      slideAnim.setValue(wdp(getByScreenSize(99, 40)));
      leftScreenOpacity.setValue(1);
      backButtonScale.setValue(0);
      backButtonRotate.setValue(0);
      titleSlideX.setValue(0);
      titleOpacity.setValue(1);
      setActiveComponent(null);
    }
  }, [
    isVisible,
    slideAnim,
    leftScreenOpacity,
    backButtonScale,
    backButtonRotate,
    titleSlideX,
    titleOpacity,
    isMobile,
  ]);

  // Handle back press from detail screen
  const handleBackPress = () => {
    if (activeComponent) {
      setActiveComponent(null);
      return true;
    } else if (isVisible) {
      closeSettingsModal();
      return true;
    }
    return false;
  };

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandlerSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandlerSubscription.remove();
      };
    }
  }, [handleBackPress]);

  // Run animation when activeComponent changes
  useEffect(() => {
    if (activeComponent) {
      // Faster, smoother animation for entering detail view
      Animated.parallel([
        // Slide in screen from right
        Animated.spring(slideAnim, {
          toValue: 0,
          friction: 9,
          tension: 80,
          useNativeDriver: Platform.OS !== 'web',
        }),
        // Fade out main screen
        Animated.timing(leftScreenOpacity, {
          toValue: 0,
          duration: 150,
          useNativeDriver: Platform.OS !== 'web',
        }),
        // Scale and rotate back button in
        Animated.spring(backButtonScale, {
          toValue: 1,
          friction: 8,
          tension: 80,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(backButtonRotate, {
          toValue: 1,
          duration: 200,
          useNativeDriver: Platform.OS !== 'web',
          easing: Easing.out(Easing.back(1.2)),
        }),
        // Animate title
        Animated.sequence([
          Animated.timing(titleOpacity, {
            toValue: 0,
            duration: 100,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.parallel([
            Animated.timing(titleOpacity, {
              toValue: 1,
              duration: 150,
              useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(titleSlideX, {
              toValue: 0,
              duration: 200,
              useNativeDriver: Platform.OS !== 'web',
              easing: Easing.out(Easing.cubic),
            }),
          ]),
        ]),
      ]).start();
    } else {
      // Faster, smoother animation for going back
      Animated.parallel([
        // Slide out detail screen
        Animated.timing(slideAnim, {
          toValue: wdp(getByScreenSize(99, 40)),
          duration: 200,
          useNativeDriver: Platform.OS !== 'web',
          easing: Easing.out(Easing.cubic),
        }),
        // Fade in main screen
        Animated.timing(leftScreenOpacity, {
          toValue: 1,
          duration: 150,
          useNativeDriver: Platform.OS !== 'web',
        }),
        // Scale and rotate back button out
        Animated.timing(backButtonScale, {
          toValue: 0,
          duration: 150,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(backButtonRotate, {
          toValue: 0,
          duration: 100,
          useNativeDriver: Platform.OS !== 'web',
        }),
        // Animate title
        Animated.sequence([
          Animated.timing(titleOpacity, {
            toValue: 0,
            duration: 100,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.parallel([
            Animated.timing(titleOpacity, {
              toValue: 1,
              duration: 150,
              useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(titleSlideX, {
              toValue: 0,
              duration: 200,
              useNativeDriver: Platform.OS !== 'web',
              easing: Easing.out(Easing.cubic),
            }),
          ]),
        ]),
      ]).start();
    }
  }, [
    activeComponent,
    slideAnim,
    leftScreenOpacity,
    backButtonScale,
    backButtonRotate,
    titleSlideX,
    titleOpacity,
  ]);

  const onPressItemHandler = (item: SettingsItem) => {
    if (item.label === 'logout') {
      openQuestionModal({
        title: tr('login.logoutTitle'),
        message: tr('login.logoutInfo'),
        onConfirm: () => {
          endSession();
          closeSettingsModal();
        },
        confirmText: tr('login.signOut'),
        rejectText: tr('app.cancel'),
        type: modalTypes.question,
      });
      return;
    }
    setActiveComponent(item);
  };

  const renderBackdrop = useCallback(
    (props: any) => {
      return (
        <BottomSheetBackdrop
          opacity={0.1}
          {...props}
          onPress={() => sheetRef.current?.close()}
          pressBehavior={'collapse'}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
        />
      );
    },
    [isMobile],
  );

  const _renderFooter = () => {
    return <Space height={hdp(10)} />;
  };

  const _renderItem = ({item}: {item: SettingsItem}) => {
    return (
      <SideMenuListItem
        onPress={() => onPressItemHandler(item)}
        iconName={item.iconName}
        iconStyle={{
          backgroundColor: theme.settings.iconBackground,
        }}
        title={tr(`settings.${item.label}`)}
        showArrow
      />
    );
  };

  // Calculate rotation interpolation
  const rotateInterpolation = backButtonRotate.interpolate({
    inputRange: [0, 1],
    outputRange: ['45deg', '0deg'],
  });

  const _renderContentModal = (): any => {
    if (component) {
      return component();
    }
    return (
      <Animated.View style={{flex: 1}}>
        <LeftScreen
          style={{opacity: leftScreenOpacity}}
          width={wdp(getByScreenSize(99, 40))}>
          <BottomSheetFlatList
            data={filteredSettingsList}
            renderItem={_renderItem}
            ListFooterComponent={_renderFooter}
            ItemSeparatorComponent={() => <Separator />}
            keyExtractor={(_: any, index: number) => `setting-item-${index}`}
          />
        </LeftScreen>
        <RightScreen
          width={wdp(getByScreenSize(99, 40))}
          isVisible={activeComponent !== null}
          style={{
            transform: [{translateX: slideAnim}],
          }}>
          {activeComponent?.component && activeComponent.component()}
        </RightScreen>
      </Animated.View>
    );
  };

  // Render web modal content
  const renderWebContent = () => {
    if (component) {
      return component();
    }

    if (activeComponent) {
      return activeComponent.component();
    }

    return (
      <WebScrollView contentContainerStyle={{paddingBottom: 20}}>
        {filteredSettingsList.map((item, index) => (
          <React.Fragment key={`setting-item-${index}`}>
            <SideMenuListItem
              onPress={() => onPressItemHandler(item)}
              iconName={item.iconName}
              iconStyle={{
                backgroundColor: theme.settings.iconBackground,
              }}
              title={tr(`settings.${item.label}`)}
              showArrow
            />
            {index < filteredSettingsList.length - 1 && <Separator />}
          </React.Fragment>
        ))}
        <Space height={hdp(5)} />
      </WebScrollView>
    );
  };

  // Method to trigger when the bottom sheet snaps to a different position
  const handleSheetChange = useCallback((index: number) => {
    if (index === -1) {
      closeSettingsModal();
    }
  }, []);

  const onClose = useCallback(() => {
    closeSettingsModal();
  }, []);

  const handleCloseBottomSheet = useCallback(() => {
    if (sheetRef.current) {
      sheetRef.current.close();
    }
  }, []);

  // Simple web modal implementation
  if (isWeb && !isMobile) {
    if (!isVisible) return null;
    return (
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1998,
        }}>
        <View
          //@ts-ignore
          style={{
            width: webModalWidth,
            maxHeight: hdp(90),
            backgroundColor: theme.screenBackground,
            borderRadius: 8,
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOpacity: 0.58,
            shadowRadius: 16,
            elevation: 24,
          }}>
          <WebHeaderRow>
            {activeComponent ? (
              <WebCloseButton onPress={() => setActiveComponent(null)}>
                <Icon
                  name={'back'}
                  size={theme.text.s6}
                  color={theme.text.dark + 'AA'}
                />
              </WebCloseButton>
            ) : (
              <WebCloseButton style={{opacity: 0}} disabled>
                <Icon
                  name={'back'}
                  size={theme.text.s6}
                  color={'transparent'}
                />
              </WebCloseButton>
            )}

            <Text
              style={{
                fontSize: theme.text.s6,
                fontFamily: getFontFamily().bold,
                fontWeight: '700',
                color: theme.text.dark,
                letterSpacing: 1,
                textAlign: 'center',
              }}>
              {title
                ? title
                : activeComponent?.component
                  ? tr(`settings.${activeComponent?.label}`)
                  : tr('settings.title')}
            </Text>

            <WebCloseButton onPress={closeSettingsModal}>
              <Icon
                name={'close'}
                size={theme.text.s6}
                color={theme.text.dark + 'AA'}
              />
            </WebCloseButton>
          </WebHeaderRow>
          {/*@ts-ignore*/}
          <View style={{maxHeight: hdp(90)}}>{renderWebContent()}</View>
        </View>
        <TouchableOpacity
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
          }}
          onPress={closeSettingsModal}
          activeOpacity={1}
        />
      </View>
    );
  } else {
    return (
      <>
        {isVisible && isWeb && (
          <View
            style={{
              width: wdp(100),
              height: hdp(100),
              position: 'absolute',
              top: 0,
              pointerEvents: 'box-none',
            }}
            onTouchStart={() => closeSettingsModal()}
            //@ts-ignore
            onClick={() => closeSettingsModal()}
          />
        )}
        <BottomSheet
          enableDynamicSizing={false}
          style={{
            borderRadius: wdp(5),
            overflow: 'hidden',
            width: wdp(getByScreenSize(99, 40)),
            marginHorizontal: wdp(0.5),
            shadowColor: '#000',
            shadowOffset: {
              width: 0,
              height: 12,
            },
            shadowOpacity: 0.58,
            shadowRadius: 16.0,
            elevation: 24,
          }}
          animationConfigs={{
            duration: 250,
          }}
          ref={sheetRef}
          index={isVisible ? 0 : -1}
          backgroundStyle={{
            backgroundColor: theme.screenBackground,
          }}
          handleComponent={props => (
            <BottomSheetHandle
              {...props}
              closeBottomSheet={handleCloseBottomSheet}
            />
          )}
          bottomInset={bottomInset}
          enablePanDownToClose
          snapPoints={snapPoints}
          backdropComponent={renderBackdrop}
          onChange={handleSheetChange}
          onClose={onClose}>
          <Row>
            <CloseIconWrapper>
              <CloseIconContainer
                onPress={handleBackPress}
                style={{
                  transform: [
                    {scale: backButtonScale},
                    {rotate: rotateInterpolation},
                  ],
                }}
                activeOpacity={0.7}>
                <Icon
                  name={'back'}
                  size={theme.text.s6}
                  color={theme.secondary + 'AA'}
                />
              </CloseIconContainer>
            </CloseIconWrapper>
            <AnimatedTitle
              style={{
                transform: [{translateX: titleSlideX}],
              }}>
              {title
                ? title
                : activeComponent?.component
                  ? tr(`settings.${activeComponent?.label}`)
                  : tr('settings.title')}
            </AnimatedTitle>
            <CloseIconWrapper>
              <CloseIconContainer
                disabled
                style={{
                  opacity: 0,
                }}>
                <Icon
                  name={'back'}
                  size={theme.text.s6}
                  color={theme.secondary + 'AA'}
                />
              </CloseIconContainer>
            </CloseIconWrapper>
          </Row>
          <Separator />
          {_renderContentModal && _renderContentModal()}
        </BottomSheet>
      </>
    );
  }
};

const mapStateToProps = (state: RootState) => ({
  settingsModal: state.settingsModal,
});

export default connect(mapStateToProps, null)(SettingsModal);
